/**
 * @fileoverview Generates the script tag for inserting on a page client side so that it is executed.
 */

import { useEffect } from 'react';

interface ClientScriptInsertInterface {
  /**
   * The piece of content that has the script tag (or tags).
   */
  content: string;
  /**
   * The unique id of the piece of content (landing page row, or job id)
   */
  id: string;
  /**
   * The identifier of the script. This is used to insert keys to avoid duplicates.
   */
  identifier: string;
}

type UseClientScriptInsertReturnType = {
  /**
   * The stripped js string (without the script tags).
   */
  strippedJsString: string;
};

/**
 * The hook that calculates referenced element's dimensions.
 *
 * @param {ClientScriptInsertInterface} initialDimensions
 *  The initial dimensions.
 * @returns {UseClientScriptInsertReturnType}
 *  Current ref and dimensions as an array.
 */
export const useClientScriptInsert = ({
  content,
  id,
  identifier,
}: ClientScriptInsertInterface): UseClientScriptInsertReturnType => {
  // Regex to get all script tags inside the string.
  const scriptTagRegex = /<script\b[^>]*>([\s\S]*?)<\/script>/gi;
  const scriptTagMatches = content && [
    ...(content as string)?.matchAll(scriptTagRegex),
  ];

  const strippedJsString = content?.replace(scriptTagRegex, '');

  useEffect(() => {
    const target = document.getElementsByTagName('head')[0];

    if (scriptTagMatches) {
      scriptTagMatches.forEach((script, index) => {
        // Create a unique id for the new script.
        const scriptId = `${identifier}-script-${id}-${index}`;
        // The full <script> tag as a string.
        const scriptTag = script[0];
        // Regex to check if the script has a "src" attribute.
        const scriptTagHasSrc = /<script\b[^>]*\ssrc\s*=\s*["'][^"']*["'][^>]*>/.test(
          scriptTag,
        );

        // Create the Element to hold the script tag (so that it can be executed).
        let executableScript = document.createElement('script');
        executableScript.id = scriptId;

        // From the scriptTag string create an actual HTML element.
        // This is needed to get the attributes for that script (so that we preserve them).
        const scriptTagElement = document.createElement('div');
        scriptTagElement.innerHTML = scriptTag;
        // Add all attributes of the scriptTag to the executableScript.
        // This is needed to get the async attribute and other possible attributes on the script element.
        const scriptTagAttributes = (scriptTagElement.firstChild as any)
          ?.attributes;
        if (scriptTagAttributes) {
          for (let i = 0; i < scriptTagAttributes.length; i++) {
            const attribute = scriptTagAttributes[i];
            executableScript.setAttribute(attribute.name, attribute.value);
          }
        }

        if (scriptTagHasSrc) {
          // Regular expression to match the value of the "src" attribute
          const scriptTagSrcRegex = /<script\b[^>]*\ssrc\s*=\s*(['"])(.*?)\1[^>]*>/i;
          const scriptTagSrcMatch = scriptTag.match(scriptTagSrcRegex);

          if (scriptTagSrcMatch) {
            // External script with a src attribute.
            const scriptTagSrc = scriptTagSrcMatch[2];
            executableScript.src = scriptTagSrc;
          }
        } else {
          // Inline script (with js inside a script tag).
          var inlineScript = document.createTextNode(script[1]);
          executableScript.appendChild(inlineScript);
        }

        // Remove the script first if it exists.
        // This avoids creating multiple scripts if the effect runs more than once.
        const scriptElement = document.getElementById(id);
        if (scriptElement) {
          target.removeChild(scriptElement);
        }
        target.appendChild(executableScript);
      });
    }

    return () => {
      // Delete any scripts added by the component.
      const scriptsToRemove = document.querySelectorAll(
        `script[id^=${identifier}-script-${id}]`,
      );
      scriptsToRemove.forEach((script) => script.remove());
    };
  }, [content, id]);

  return {
    strippedJsString,
  };
};
